import React, { useCallback, useContext, useEffect } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import {
  getLocales as getLocaleOptions,
  getLocalizations,
  setEditMode as setEditModeAction
} from '../redux/localizationSlice';
import Loading from '../components/Loading';
import EditableLocalization from '../components/EditableLocalization';
import { useSearchParams } from 'react-router-dom';

const LocalizationContext = React.createContext();

export const useLocalization = () => useContext(LocalizationContext);

export function LocalizationProvider({ children }) {
  const state = useSelector((state) => state.localizations);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const getLocales = useCallback(() => {
    dispatch(getLocaleOptions());
  }, []);

  const setLocale = useCallback((locale) => {
    dispatch(getLocalizations(locale));
  }, []);

  const setEditMode = useCallback((value) => {
    dispatch(setEditModeAction(value));
  }, []);

  useEffect(() => {
    if (state.locale) {
      document.documentElement.lang = state.locale;
    }
  }, [state.locale]);

  useEffect(() => {
    if (!state.options?.length || !state.messages) {
      getLocales();
      let locale = searchParams.get('lang') || window.localStorage.getItem('lang') || state.defaultLang;
      setLocale(state.options.includes(locale) ? locale : null);
    }
  }, []);

  return (
    <IntlProvider
      locale={state.locale || 'fi'}
      messages={state.messages || {}}
      onError={() => null}>
      {state.messages && (
        <LocalizationWrapperProvider
          state={state}
          setLocale={setLocale}
          setEditMode={setEditMode}>
          {state.loading && <Loading />}
          {children}
        </LocalizationWrapperProvider>
      )}
    </IntlProvider>
  );
}

function LocalizationWrapperProvider({
  state,
  setLocale,
  setEditMode,
  children
}) {
  const intl = useIntl();

  const toggleEditMode = useCallback(() => {
    setEditMode(!state.edit.enabled);
  }, [state.edit.enabled]);

  const localizeObject = useCallback(
    (obj) => {
      if (typeof obj === 'string') {
        return obj;
      }

      let value = obj?.[state.locale] || obj?.[state.defaultLang];
      return value || Object.values(obj || {}).find((value) => typeof value === 'string' && value);
    },
    [state.locale, state.defaultLang]
  );

  const localize = useCallback(
    (id, options = {}) => {
      if (!state.locale) return id;

      if (typeof id !== 'string') {
        return localizeObject(id);
      }

      if (!state?.messages?.[id]) return id;

      const { values, editable, ...rest } = options;
      const message = intl.formatMessage({ id, ...rest }, values);

      if (state.edit.enabled && editable !== false) {
        return <EditableLocalization id={id} message={message} />;
      }

      return message;
    },
    [state.locale, state.edit.enabled, state.messages]
  );

  const format = useCallback(
    (type, value, options = null) => {
      if (!state.locale) return value;

      switch (type) {
        case 'language':
          return intl.formatDisplayName(value, { type });

        case 'year':
          return intl.formatDate(
            new Date(value),
            options || {
              year: 'numeric'
            }
          );
        case 'date':
          if (!value) return null;
          return intl.formatDate(
            new Date(value),
            options || {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric'
            }
          );
        case 'datetime':
          if (!value) return null;
          return intl.formatDate(
            new Date(value),
            options || {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric'
            }
          );
        case 'currency':
          return new Intl.NumberFormat('fi', {
            style: 'currency',
            ...(options || { currency: 'EUR' })
          }).format(value || 0);
        default:
          return value;
      }
    },
    [state.locale]
  );

  const compare = useCallback(
    (a, b) => {
      return (localizeObject(a) || '').localeCompare((localizeObject(b) || ''), state.locale, {
        sensitivity: 'base'
      }) || 0;
    },
    [state.locale]
  );

  localize.format = useCallback(format, [format]);
  localize.compare = useCallback(compare, [compare]);

  return (
    <LocalizationContext.Provider
      value={{
        locale: state.locale,
        locales: state.options,
        sortedLocales: state.sortedOptions,
        loading: state.loading,
        messages: state.messages,
        edit: state.edit,
        toggleEditMode,
        localize,
        setLocale: (locale) => setLocale(locale)
      }}>
      {children}
    </LocalizationContext.Provider>
  );
}
